// Markup for the redesigned event popup shown under the graph (the tray below
// chartArea.bottom). Figma "Events on graph improvements" 12009:11615.
//
// Three rows separated by hairlines:
//   1. event icon + amount, pressure reading, and the red pressure-alert line
//   2. clock icon + start / end timestamps
//   3. source icon + sensor name
//
// React-free and icon-agnostic: every image src is passed in by EventPopUp.jsx
// (which owns the webpack `require('images/...')` calls), so this module can be
// unit-tested with plain `node --test`.

const {
  buildGraphPressureRows,
  POPUP_TEXT_STYLE,
} = require('./eventPopUpHelpers');

// Every row reserves the same 24px icon slot so all three rows' text starts on
// one vertical line, even where the glyph itself is smaller (the piezometer is
// drawn at 16px inside its slot).
const ICON_SLOT = 24;
const SOURCE_ICON_SIZE = 16;
const ROW_STYLE = 'display: flex;gap: 8px;align-items: center';
const CARD_STYLE =
  'display: flex;flex-direction: column;gap: 8px;padding: 4px 0';
const SEPARATOR =
  "<div style='height: 1px;background: var(--color-grey-91)'></div>";
// Rows without an icon indent their text by the slot width plus the row gap.
const ICONLESS_INDENT = 'padding-left: ' + (ICON_SLOT + 8) + 'px';
const TIME_COLUMN_STYLE =
  'display: flex;flex-direction: column;flex: 0 0 auto;line-height: 1.2;' +
  'color: var(--color-grey-71)';

// The caret only ever appears on the popup opened from the pressure-alert
// badge. Two stacked triangles: the border-coloured one behind, a white one
// nudged up over it, leaving only the card's 1px outline along the slopes.
const ARROW_HEIGHT = 6;
const ARROW_HALF_WIDTH = 6;

// A glyph centred in the shared 24px slot. `size` defaults to filling the slot.
const iconTag = (src, size) => {
  const glyph = size || ICON_SLOT;
  return (
    "<div style='display: flex;align-items: center;justify-content: center;" +
    'flex: 0 0 auto;width: ' +
    ICON_SLOT +
    'px;height: ' +
    ICON_SLOT +
    "px'>" +
    "<img style='width: " +
    glyph +
    'px;height: ' +
    glyph +
    "px' src='" +
    src +
    "'/>" +
    '</div>'
  );
};

// Sits outside the card's bottom edge, pointing down at the pressure-alert
// badge the popup was opened from.
const buildArrow = () => {
  const triangle = (color, offsetY) =>
    "<div style='position: absolute;left: 0;top: " +
    offsetY +
    'px;width: 0;height: 0;' +
    'border-left: ' +
    ARROW_HALF_WIDTH +
    'px solid transparent;border-right: ' +
    ARROW_HALF_WIDTH +
    'px solid transparent;' +
    'border-top: ' +
    ARROW_HEIGHT +
    'px solid ' +
    color +
    "'></div>";
  // Centred on the card, which is itself centred on the badge — so the caret
  // lands on the badge's centre without either having to know the other's
  // geometry.
  return (
    "<div style='position: absolute;bottom: -" +
    (ARROW_HEIGHT + 1) +
    'px;left: 50%;transform: translateX(-50%);width: ' +
    ARROW_HALF_WIDTH * 2 +
    'px;height: ' +
    ARROW_HEIGHT +
    "px'>" +
    triangle('var(--color-grey-91)', 0) +
    triangle('var(--color-grey-100)', -1.5) +
    '</div>'
  );
};

const semiBoldText = (text, extraStyle) =>
  "<div class='font-weight-semi-bold' style='" +
  POPUP_TEXT_STYLE +
  (extraStyle ? ';' + extraStyle : '') +
  "'>" +
  text +
  '</div>';

// Row 1 — the measurement block. `extraText` carries the event type's own
// second line (fertilization's "CONTAINING x kg/ha"). The delete control lives
// on the time-frame row, not here.
const buildMeasurementRow = (options) => {
  const pressureRows = buildGraphPressureRows({
    pressure: options.pressure,
    pressureUnit: options.pressureUnit,
    pressureLabel: options.pressureLabel,
    alertLevel: options.pressureAlert,
    alertIconSrc: options.alertIconSrc,
    t: options.t,
  });
  return (
    "<div style='" +
    ROW_STYLE +
    "'>" +
    iconTag(options.eventIconSrc) +
    "<div style='display: flex;flex-direction: column;justify-content: center'>" +
    semiBoldText(options.amountText) +
    (options.extraText ? semiBoldText(options.extraText) : '') +
    pressureRows +
    '</div>' +
    '</div>'
  );
};

// A date-over-time stack: "9 JUN" above "8:30 am". `white-space: nowrap` keeps
// the meridiem on the same line as the time — in a narrow popup "8:30" and "am"
// were breaking apart — and `flex: 0 0 auto` stops the column being squeezed
// below its content width by the row's other children.
const buildTimeColumn = (dateStr, timeStr) =>
  "<div style='" +
  TIME_COLUMN_STYLE +
  "'>" +
  "<div style='font-size: 12px;white-space: nowrap'>" +
  dateStr +
  '</div>' +
  "<div class='font-weight-semi-bold' style='font-size: 14px;white-space: nowrap'>" +
  timeStr +
  '</div>' +
  '</div>';

// Row 2 — the time frame, and the delete control. Fertilization events on the
// graph have no end date, so the dash and end column are dropped rather than
// rendered empty. `deleteRow` is the existing trash-icon markup built by
// EventPopUp, pushed to the far right of this row (it is `d-none` when the user
// may not delete, so the row is unchanged then).
const buildTimeFrameRow = (options) => {
  const endBlock = options.hideEndDate
    ? ''
    : "<div style='" +
      POPUP_TEXT_STYLE +
      "'>-</div>" +
      buildTimeColumn(options.endDateStr, options.endTimeStr);
  return (
    "<div style='" +
    ROW_STYLE +
    "'>" +
    iconTag(options.timerIconSrc) +
    buildTimeColumn(options.begDateStr, options.begTimeStr) +
    endBlock +
    "<div style='margin-left: auto;display: flex;align-items: center'>" +
    (options.deleteRow || '') +
    '</div>' +
    '</div>'
  );
};

// Row 3 — where the reading came from. Only sensor-sourced events carry an
// icon; manual entries and rain gauges show the name alone, indented to match.
// Omitted entirely when the event has no source to name.
const buildSourceRow = (options) => {
  if (!options.sourceText) {
    return '';
  }
  const body = options.sourceIconSrc
    ? iconTag(options.sourceIconSrc, SOURCE_ICON_SIZE) +
      semiBoldText(options.sourceText)
    : semiBoldText(options.sourceText, ICONLESS_INDENT);
  return "<div style='" + ROW_STYLE + "'>" + body + '</div>';
};

// Assembles the card: one event per popup. Separators are emitted only between
// rows that exist, so a sourceless event does not end on a dangling hairline.
// The caret is added only for the badge-anchored popup (`showArrow`); it is a
// sibling of the rows, positioned against the popup itself (.dropdown-menu is
// already absolutely positioned, so it is the caret's containing block).
const buildGraphEventCard = (options) => {
  const rows = [buildMeasurementRow(options), buildTimeFrameRow(options)];
  const sourceRow = buildSourceRow(options);
  if (sourceRow) {
    rows.push(sourceRow);
  }
  return (
    "<div style='" +
    CARD_STYLE +
    "'>" +
    rows.join(SEPARATOR) +
    '</div>' +
    (options.showArrow ? buildArrow() : '')
  );
};

module.exports = {
  buildGraphEventCard,
  buildMeasurementRow,
  buildTimeFrameRow,
  buildSourceRow,
  buildTimeColumn,
  buildArrow,
  SEPARATOR,
  ICONLESS_INDENT,
  ICON_SLOT,
  SOURCE_ICON_SIZE,
  ARROW_HEIGHT,
  ARROW_HALF_WIDTH,
};
